import { FC, Fragment, useMemo, useReducer } from 'react';

import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { isNonNullish, isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { options } from './constants';
import { AddFreeSpinsForm } from './form';
import { FreeSpinsInfoParams } from './freeSpinsInfoParams/FreeSpinsInfoParams';

import { providerService } from 'v2/api/providerService';
import { Select } from 'v2/components/select/Select';
import { ProviderName } from 'v2/constants';
import { handleHttpError } from 'v2/utils';

interface AddFreeSpinsButtonProps {
  onSuccess: VoidFunction;
}

export const AddFreeSpinsButton: FC<AddFreeSpinsButtonProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const formik = useFormik<AddFreeSpinsForm>({
    initialValues: {
      providerName: ProviderName.BGAMING,
      gameId: '',
      gameName: '',
      countSpins: 0,
      betSpins: 0,
    },
    onSubmit: (values) => triggerCreateFreeSpins(values),
  });

  const { data: providerGames, error } = useSWR(
    isOpen && ['getProviderGames', formik.values.providerName],
    providerService.getProviderGames
  );

  const {
    data: providerGameInfo,
    trigger: triggerProviderGameInfo,
    reset,
  } = useSWRMutation(['getProviderGameInfo', formik.values.providerName], providerService.getProviderGameInfo);

  const { trigger: triggerCreateFreeSpins, isMutating } = useSWRMutation(
    'createFreeSpins',
    providerService.createFreeSpins,
    {
      onSuccess: () => {
        onSuccess();
        toast.success('The free spins have been successfully added');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const gamesOptions = useMemo(() => {
    if (isNullish(providerGames)) return [];

    return providerGames.map((g) => ({ id: g.gameId, value: g.gameId, label: g.name }));
  }, [providerGames]);

  const handleChangeProvider = (value: ProviderName) => {
    formik.resetForm();
    reset();
    formik.setFieldValue('providerName', value);
  };

  const handleChangeGameId = (value: string) => {
    formik.setFieldValue('gameId', value);
    formik.setFieldValue('gameName', gamesOptions.find((o) => o.value === value)?.label);

    triggerProviderGameInfo(value);
  };

  const handleChangeSelect =
    (field: keyof AddFreeSpinsForm) =>
    <T,>(value: T) => {
      formik.setFieldValue(field, value);
    };

  const handleClose = () => {
    setOpen();
    formik.resetForm();
    reset();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Plus />} variant="contained" size="large">
        Add Free Spins
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Free Spins</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="form">
            <Select
              value={formik.values.providerName}
              options={options}
              label="Provider"
              onChange={handleChangeProvider}
              required
            />

            {!error ? (
              <div className="form-group">
                <Select
                  value={formik.values.gameId}
                  options={gamesOptions}
                  label="Game Id"
                  onChange={handleChangeGameId}
                  required
                />

                <TextField
                  name="gameName"
                  value={formik.values.gameName}
                  label="Game Name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                  fullWidth
                />
              </div>
            ) : (
              <Alert severity="error">Something went wrong</Alert>
            )}

            {isNonNullish(providerGameInfo) && (
              <FreeSpinsInfoParams
                info={providerGameInfo}
                values={formik.values}
                onChange={formik.handleChange}
                onSelectChange={handleChangeSelect}
              />
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button loading={isMutating} type="submit" variant="contained">
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
